import type { ErrorCode } from '@nadoutong/contracts';
import { isErrorCode } from '@nadoutong/contracts';
import type { I18nKey } from '../i18n/index.ts';
import type { TParams } from '../i18n/types.ts';
import { PlatformApiError } from './client.ts';

/**
 * User-facing text for a failed request.
 *
 * Overrides win, then the central `error.code.*` table. Status 0 is a
 * transport failure. A non-envelope body is stamped
 * `SERVICE_UNAVAILABLE` by `client.ts`; a 5xx of that shape is a
 * generic service failure, not the server's own code. Anything else
 * unmapped — including plain errors and strings — uses
 * `error_banner.unexpected`. The raw `CODE: message` form is never
 * returned.
 */

export type MessageKey = I18nKey;

type Translate = (key: MessageKey, params?: TParams) => string;

const NETWORK_MESSAGE: MessageKey = 'error.network';
const SERVICE_MESSAGE: MessageKey = 'error.service';
const FALLBACK_MESSAGE: MessageKey = 'error_banner.unexpected';

const SYNTHETIC_NON_ENVELOPE_PREFIX = 'platform-api returned ';

export const ERROR_CODE_MESSAGES = {
  EMAIL_NOT_VERIFIED: 'error.code.EMAIL_NOT_VERIFIED',
  INVALID_CREDENTIALS: 'error.code.INVALID_CREDENTIALS',
  ACCOUNT_SUSPENDED: 'error.code.ACCOUNT_SUSPENDED',
  ACCOUNT_DELETED: 'error.code.ACCOUNT_DELETED',
  RATE_LIMITED: 'error.code.RATE_LIMITED',
  EMAIL_ALREADY_REGISTERED: 'error.code.EMAIL_ALREADY_REGISTERED',
  EMAIL_FORMAT_INVALID: 'error.code.EMAIL_FORMAT_INVALID',
  PASSWORD_LENGTH_INVALID: 'error.code.PASSWORD_LENGTH_INVALID',
  INVALID_VERIFICATION_TOKEN: 'error.code.INVALID_VERIFICATION_TOKEN',
  VERIFICATION_TOKEN_EXPIRED: 'error.code.VERIFICATION_TOKEN_EXPIRED',
  VERIFICATION_TOKEN_CONSUMED: 'error.code.VERIFICATION_TOKEN_CONSUMED',
  AUTH_REQUIRED: 'error.code.AUTH_REQUIRED',
  FORBIDDEN: 'error.code.FORBIDDEN',
  INVALID_PAYLOAD: 'error.code.INVALID_PAYLOAD',
  CODE_INVALID: 'error.code.CODE_INVALID',
  CODE_EXPIRED: 'error.code.CODE_EXPIRED',
  CODE_RATE_LIMITED: 'error.code.CODE_RATE_LIMITED',
  INVITE_CODE_INVALID: 'error.code.INVITE_CODE_INVALID',
  INVITE_CODE_QUOTA_EXHAUSTED: 'error.code.INVITE_CODE_QUOTA_EXHAUSTED',
  INVITATION_EXPIRED: 'error.code.INVITATION_EXPIRED',
  INVITATION_REVOKED: 'error.code.INVITATION_REVOKED',
  INVITATION_ALREADY_ACCEPTED: 'error.code.INVITATION_ALREADY_ACCEPTED',
  INVITATION_NOT_FOUND: 'error.code.INVITATION_NOT_FOUND',
  INVITATION_CONFLICT: 'error.code.INVITATION_CONFLICT',
  INSUFFICIENT_BALANCE: 'error.code.INSUFFICIENT_BALANCE',
  TOPUP_AMOUNT_TOO_SMALL: 'error.code.TOPUP_AMOUNT_TOO_SMALL',
  TOPUP_AMOUNT_TOO_LARGE: 'error.code.TOPUP_AMOUNT_TOO_LARGE',
  TOPUP_PROVIDER_ERROR: 'error.code.TOPUP_PROVIDER_ERROR',
  PAYMENT_ORDER_NOT_FOUND: 'error.code.PAYMENT_ORDER_NOT_FOUND',
  PAYMENT_PROVIDER_CONFIG_NOT_FOUND: 'error.code.PAYMENT_PROVIDER_CONFIG_NOT_FOUND',
  GIFT_CARD_ALREADY_REDEEMED: 'error.code.GIFT_CARD_ALREADY_REDEEMED',
  GIFT_CARD_EXPIRED: 'error.code.GIFT_CARD_EXPIRED',
  GIFT_CARD_NOT_FOUND: 'error.code.GIFT_CARD_NOT_FOUND',
  GIFT_CARD_REVOKED: 'error.code.GIFT_CARD_REVOKED',
  PROMOTER_CODE_INVALID: 'error.code.PROMOTER_CODE_INVALID',
  PROMOTER_ALREADY_BOUND: 'error.code.PROMOTER_ALREADY_BOUND',
  PROMOTER_BIND_WINDOW_CLOSED: 'error.code.PROMOTER_BIND_WINDOW_CLOSED',
  PROMOTER_SELF_BIND_FORBIDDEN: 'error.code.PROMOTER_SELF_BIND_FORBIDDEN',
  PROMOTER_BIND_CONFLICT: 'error.code.PROMOTER_BIND_CONFLICT',
  ORG_NOT_FOUND: 'error.code.ORG_NOT_FOUND',
  ORG_ROLE_NOT_FOUND: 'error.code.ORG_ROLE_NOT_FOUND',
  ORG_ROLE_NAME_TAKEN: 'error.code.ORG_ROLE_NAME_TAKEN',
  ORG_ROLE_IN_USE: 'error.code.ORG_ROLE_IN_USE',
  KEY_NOT_FOUND: 'error.code.KEY_NOT_FOUND',
  ADMIN_KEY_LIMIT_REACHED: 'error.code.ADMIN_KEY_LIMIT_REACHED',
  API_KEY_NOT_FOUND: 'error.code.API_KEY_NOT_FOUND',
  MODEL_ALIAS_NOT_FOUND: 'error.code.MODEL_ALIAS_NOT_FOUND',
  MODEL_ALIAS_ALREADY_EXISTS: 'error.code.MODEL_ALIAS_ALREADY_EXISTS',
  MODEL_MAPPING_STALE: 'error.code.MODEL_MAPPING_STALE',
  MODEL_MAPPING_ROLLOUT_PENDING: 'error.code.MODEL_MAPPING_ROLLOUT_PENDING',
  MEMBER_MODEL_FORBIDDEN: 'error.code.MEMBER_MODEL_FORBIDDEN',
  MEMBER_MONTHLY_LIMIT_EXCEEDED: 'error.code.MEMBER_MONTHLY_LIMIT_EXCEEDED',
  ATTACHMENT_NOT_FOUND: 'error.code.ATTACHMENT_NOT_FOUND',
  SERVICE_UNAVAILABLE: 'error.code.SERVICE_UNAVAILABLE',
  INTERNAL: 'error.code.INTERNAL',
  NOT_IMPLEMENTED: 'error.code.NOT_IMPLEMENTED',
  TICKET_NOT_FOUND: 'error.code.TICKET_NOT_FOUND',
  TICKET_CLOSED: 'error.code.TICKET_CLOSED',
  TICKET_LIMIT_EXCEEDED: 'error.code.TICKET_LIMIT_EXCEEDED',
} as const satisfies Partial<Record<ErrorCode, MessageKey>>;

function isSyntheticNonEnvelope(err: PlatformApiError): boolean {
  return (
    err.code === 'SERVICE_UNAVAILABLE' && err.message.startsWith(SYNTHETIC_NON_ENVELOPE_PREFIX)
  );
}

export function localizeApiError(
  err: unknown,
  t: Translate,
  overrides?: Partial<Record<ErrorCode, MessageKey>>,
): string {
  if (!(err instanceof PlatformApiError)) {
    return t(FALLBACK_MESSAGE);
  }
  if (err.status === 0) {
    return t(NETWORK_MESSAGE);
  }
  if (isSyntheticNonEnvelope(err)) {
    return err.status >= 500 ? t(SERVICE_MESSAGE) : t(FALLBACK_MESSAGE);
  }

  const code = isErrorCode(err.code) ? err.code : null;
  if (code === null) {
    return err.status >= 500 ? t(SERVICE_MESSAGE) : t(FALLBACK_MESSAGE);
  }

  const override = overrides?.[code];
  if (override !== undefined) {
    return t(override);
  }
  const mapped = ERROR_CODE_MESSAGES[code as keyof typeof ERROR_CODE_MESSAGES];
  if (mapped !== undefined) {
    return t(mapped);
  }
  return t(FALLBACK_MESSAGE);
}

/**
 * Platform failures go through {@link localizeApiError}. Anything else
 * keeps the caller's already-translated fallback instead of a raw
 * English `Error.message`.
 */
export function localizeCaughtError(
  err: unknown,
  t: Translate,
  fallback: MessageKey,
  overrides?: Partial<Record<ErrorCode, MessageKey>>,
): string {
  if (err instanceof PlatformApiError) {
    return localizeApiError(err, t, overrides);
  }
  return t(fallback);
}
